import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '../api'

export type ChecklistType = 'checkout' | 'checkin' | 'inspection'
export type ChecklistStatus = 'in_progress' | 'completed'
export type ItemCondition = 'excellent' | 'good' | 'fair' | 'poor' | 'damaged' | 'missing'

export interface TurnoverChecklist {
  id: string
  reservation_id: string | null
  checklist_type: ChecklistType
  status: ChecklistStatus
  started_by: string
  started_at: string
  completed_at: string | null
  notes: string | null
  started_by_name?: string
  reservation_title?: string
  reservation_check_out?: string
}

export interface ChecklistItem {
  id: string
  checklist_id: string
  item_type: 'inventory' | 'consumable'
  item_id: string
  photo_url: string | null
  photo_key: string | null
  condition: ItemCondition | null
  quantity_recorded: number | null
  notes: string | null
  checked_at: string
  item_name?: string
  item_photo_url?: string
  item_location_name?: string
}

export interface ItemToCheck {
  id: string
  name: string
  photo_url: string | null
  location_name: string | null
  item_type: 'inventory' | 'consumable'
  // For inventory items
  current_condition?: string
  // For consumables
  current_quantity?: number
  unit?: string
  min_quantity?: number
  max_quantity?: number
}

export const useChecklistsStore = defineStore('checklists', () => {
  const checklists = ref<TurnoverChecklist[]>([])
  const currentChecklist = ref<TurnoverChecklist | null>(null)
  const currentItems = ref<ChecklistItem[]>([])
  const itemsToCheck = ref<{ inventory_items: ItemToCheck[]; consumables: ItemToCheck[] }>({
    inventory_items: [],
    consumables: [],
  })
  const loading = ref(false)
  const error = ref<string | null>(null)

  const inProgressChecklists = computed(() =>
    checklists.value.filter(c => c.status === 'in_progress')
  )

  const completedChecklists = computed(() =>
    checklists.value.filter(c => c.status === 'completed')
  )

  const allItemsToCheck = computed(() => [
    ...itemsToCheck.value.inventory_items,
    ...itemsToCheck.value.consumables,
  ])

  const checkedItemIds = computed(() =>
    new Set(currentItems.value.map(item => `${item.item_type}:${item.item_id}`))
  )

  const uncheckedItems = computed(() =>
    allItemsToCheck.value.filter(item => !checkedItemIds.value.has(`${item.item_type}:${item.id}`))
  )

  const progress = computed(() => {
    const total = allItemsToCheck.value.length
    const checked = currentItems.value.length
    return total > 0 ? Math.round((checked / total) * 100) : 0
  })

  async function fetchChecklists(status?: ChecklistStatus) {
    loading.value = true
    error.value = null

    try {
      const params = status ? `?status=${status}` : ''
      const response = await api.get(`/api/checklists${params}`)
      checklists.value = response.checklists
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch checklists'
    } finally {
      loading.value = false
    }
  }

  async function fetchChecklist(id: string) {
    loading.value = true
    error.value = null

    try {
      const response = await api.get(`/api/checklists/${id}`)
      currentChecklist.value = response.checklist
      currentItems.value = response.items
      return response.checklist
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch checklist'
      return null
    } finally {
      loading.value = false
    }
  }

  async function fetchItemsToCheck() {
    try {
      const response = await api.get('/api/checklists/items-to-check')
      itemsToCheck.value = {
        inventory_items: response.inventory_items.map((item: any) => ({ ...item, item_type: 'inventory' })),
        consumables: response.consumables.map((item: any) => ({ ...item, item_type: 'consumable' })),
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch items to check'
    }
  }

  async function createChecklist(data: { reservation_id?: string; checklist_type: ChecklistType; notes?: string }) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/api/checklists', data)
      currentChecklist.value = response.checklist
      currentItems.value = []
      await fetchItemsToCheck()
      return response.checklist
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create checklist'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function addItemCheck(checklistId: string, data: {
    item_type: 'inventory' | 'consumable'
    item_id: string
    photo_key?: string
    condition?: ItemCondition
    quantity_recorded?: number
    notes?: string
  }) {
    try {
      const response = await api.post(`/api/checklists/${checklistId}/items`, data)

      // Add to current items if not already there
      if (!response.updated) {
        const item = allItemsToCheck.value.find(i => i.item_type === data.item_type && i.id === data.item_id)
        if (item) {
          currentItems.value.push({
            id: response.item_check_id,
            checklist_id: checklistId,
            item_type: data.item_type,
            item_id: data.item_id,
            photo_url: data.photo_key ? `/api/checklists/photo/${data.photo_key}` : null,
            photo_key: data.photo_key || null,
            condition: data.condition || null,
            quantity_recorded: data.quantity_recorded ?? null,
            notes: data.notes || null,
            checked_at: new Date().toISOString(),
            item_name: item.name,
            item_location_name: item.location_name,
          })
        }
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to save item check'
      throw err
    }
  }

  async function completeChecklist(checklistId: string, notes?: string) {
    try {
      await api.patch(`/api/checklists/${checklistId}`, { status: 'completed', notes })
      if (currentChecklist.value?.id === checklistId) {
        currentChecklist.value.status = 'completed'
        currentChecklist.value.completed_at = new Date().toISOString()
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to complete checklist'
      throw err
    }
  }

  async function deleteChecklist(id: string) {
    try {
      await api.delete(`/api/checklists/${id}`)
      checklists.value = checklists.value.filter(c => c.id !== id)
      if (currentChecklist.value?.id === id) {
        currentChecklist.value = null
        currentItems.value = []
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete checklist'
      throw err
    }
  }

  async function getUploadUrl(filename: string, contentType: string) {
    const response = await api.post('/api/checklists/upload-url', { filename, contentType })
    return response
  }

  async function uploadPhoto(uploadUrl: string, file: File): Promise<string> {
    const API_BASE = import.meta.env.VITE_API_URL || ''
    const token = localStorage.getItem('session_token')

    const response = await fetch(`${API_BASE}${uploadUrl}`, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        ...(token ? { 'X-Session-Token': token } : {}),
      },
      body: file,
    })

    if (!response.ok) {
      throw new Error('Failed to upload photo')
    }

    const data = await response.json()
    return data.key
  }

  function clearCurrent() {
    currentChecklist.value = null
    currentItems.value = []
  }

  return {
    checklists,
    currentChecklist,
    currentItems,
    itemsToCheck,
    allItemsToCheck,
    checkedItemIds,
    uncheckedItems,
    progress,
    inProgressChecklists,
    completedChecklists,
    loading,
    error,
    fetchChecklists,
    fetchChecklist,
    fetchItemsToCheck,
    createChecklist,
    addItemCheck,
    completeChecklist,
    deleteChecklist,
    getUploadUrl,
    uploadPhoto,
    clearCurrent,
  }
})
